import { useState, useEffect } from "react";
import { getIn } from "formik";
import {
  Box,
  Button,
  Typography,
  LinearProgress,
  IconButton,
  FormControl,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { Icon } from "@iconify/react";
import CustomDivider from "../../utils/CustomDivider";

export const DropzoneUploadFormFileMultiple = ({
  field,
  form,
  label,
  multiple = true,
  setFilesRef,
}) => {
  const errorText =
    getIn(form.touched, field.name) && getIn(form.errors, field.name);

  // handle local file state (supports both URL and File)
  const [files, setFiles] = useState(() => {
    const initial = field.value || [];
    if (Array.isArray(initial)) {
      return initial.map((item) =>
        typeof item === "string"
          ? { file: item, progress: 100 }
          : { file: item, progress: 100 }
      );
    }
    return [];
  });

  useEffect(() => {
    const valid = files.map((f) => f.file);
    form.setFieldValue(field.name, valid, false);
  }, [files]);


  useEffect(() => {
    if (setFilesRef) {
      setFilesRef.current = setFiles;
    }
  }, [setFilesRef]);





  const validateFile = (file) => {
    const maxSize = 2 * 1024 * 1024; // 2 MB
    const allowedTypes = [
      "image/jpeg",
      "image/png",
      // "application/pdf",
      // "video/mp4",
    ];
    if (!allowedTypes.includes(file.type)) {
      return "Only JPEG, PNG files are allowed.";
    }
    if (file.size > maxSize) {
      return "File size should be less than 2MB.";
    }
    return null;
  };

  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length === 0) return;

    const newFiles = [];
    for (const file of selectedFiles) {
      const error = validateFile(file);
      if (error) {
        form.setFieldError(field.name, error);
        return;
      }
      newFiles.push({ file, progress: 100 });
    }

    setFiles((prev) => [...prev, ...newFiles]);
    event.target.value = "";
  };

  const onRemove = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const renderFileIcon = (fileObj) => {
    const file = fileObj.file;
    if (!file) return null;

    // ✅ if it's a string (from backend)
    if (typeof file === "string") {
      const url = file.startsWith("http") ? file : `${file}`;
      const name = file.split("/").pop();
      return (
        <img
          src={url}
          alt={name}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      );
    }

    // ✅ if it's a File
    if (file.type.includes("image")) {
      const previewUrl = URL.createObjectURL(file);
      return (
        <img
          src={previewUrl}
          alt={file.name}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
          onLoad={() => URL.revokeObjectURL(previewUrl)}
        />
      );
    }

    if (file.type === "application/pdf")
      return <CloudUploadIcon sx={{ fontSize: 30, color: "red" }} />;
    if (file.type === "video/mp4")
      return <CloudUploadIcon sx={{ fontSize: 30, color: "purple" }} />;

    return <CloudUploadIcon sx={{ fontSize: 30, color: "#808080" }} />;
  };

  return (
    <FormControl fullWidth>
      {/* Upload box */}
      <Box
        sx={{
          border: "2px dashed #ccc",
          borderRadius: 2,
          p: 4,
          textAlign: "center",
          "&:hover": { borderColor: "#888" },
        }}
      >
        <Icon icon="streamline-ultimate:cloud-add" width="35" height="35" />
        <CustomDivider size={10} />
        <Typography variant="body1">
          {label || "Choose files or drag & drop them here"}
        </Typography>
        <CustomDivider size={5} />
        <Typography variant="body2" color="grey.600">
          JPEG, PNG (max 2MB each)
        </Typography>
        <CustomDivider size={20} />
        <Button
          variant="outlined"
          component="label"
          sx={{
            border: "2px solid #646464",
            borderRadius: "2rem",
            textTransform: "none",
          }}
        >
          <Typography color="#646464">Browse Files</Typography>
          <input
            type="file"
            hidden
            multiple={multiple}
            onChange={handleFileSelect}
            accept=".jpeg,.jpg,.png,.pdf,.mp4"
          />
        </Button>
      </Box>

      {/* File list */}
      {files.length > 0 ? (
        <Box sx={{ mt: 3 }}>
          {files.map((fileObj, index) => {
            const file = fileObj.file;
            const sizeMB =
              typeof file === "string"
                ? null
                : (file.size / (1024 * 1024)).toFixed(2);

            return (
              <Box
                key={index}
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "#f7f9fc",
                  position: "relative",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: "6px",
                      overflow: "hidden",
                      bgcolor: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {renderFileIcon(fileObj)}
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 500, wordBreak: "break-all" }}
                    >
                      {typeof file === "string"
                        ? file.split("/").pop()
                        : file.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {sizeMB ? `${sizeMB} MB • Completed` : "Uploaded"}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={fileObj.progress}
                      sx={{
                        mt: 1,
                        height: 4,
                        borderRadius: 2,
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: "#375EF9",
                        },
                      }}
                    />
                  </Box>

                  <IconButton
                    onClick={() => onRemove(index)}
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      border: "1px solid #292D32",
                      borderRadius: "50%",
                      p: 0.5,
                      bgcolor: "#fff",
                      "&:hover": { bgcolor: "#f0f0f0" },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1, textAlign: "center" }}
        >
          No attachments uploaded
        </Typography>
      )}

      {/* Error display */}
      {errorText && (
        <Typography variant="body2" color="error.main" sx={{ mt: 0.5 }}>
          {errorText}
        </Typography>
      )}
    </FormControl>
  );
};
